import React, { useState, useEffect, useRef } from 'react';

// Helper function to generate unique IDs from heading text
const generateHeadingId = (text: string, existingIds: Set<string> = new Set()): string => {
  let baseId = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
  
  // Handle empty or invalid IDs
  if (!baseId) {
    baseId = 'heading';
  }
  
  // Ensure uniqueness
  let finalId = baseId;
  let counter = 1;
  while (existingIds.has(finalId)) {
    finalId = `${baseId}-${counter}`;
    counter++;
  }
  
  return finalId;
};

export const enhanceHtmlContent = (content: string): string => {
  if (!content) return '';

  let enhanced = content;
  const existingIds = new Set<string>();

  // First pass: extract existing heading IDs to avoid duplicates
  const headingRegex = /<(h[1-6])[^>]*id="([^"]*)"[^>]*>/gi;
  let match;
  while ((match = headingRegex.exec(enhanced)) !== null) {
    existingIds.add(match[2]);
  }

  // Add IDs to headings that don't have them
  enhanced = enhanced.replace(
    /<(h[1-6])([^>]*?)>/gi,
    (match, tagName, attributes) => {
      // Check if heading already has an ID
      if (attributes.includes('id=')) {
        return match; // Keep existing ID
      }
      
      // Extract text content for ID generation
      const textMatch = enhanced.match(new RegExp(`${match}(.*?)</${tagName}>`, 'i'));
      const text = textMatch ? textMatch[1].replace(/<[^>]*>/g, '').trim() : '';
      
      if (text) {
        const id = generateHeadingId(text, existingIds);
        existingIds.add(id);
        return `<${tagName}${attributes} id="${id}">`;
      }
      
      return match;
    }
  );

  // Add target="_blank" to external links
  enhanced = enhanced.replace(
    /<a\s+href="(https?:\/\/[^"]+)"([^>]*)>/gi,
    '<a href="$1" target="_blank" rel="noopener noreferrer"$2>'
  );

  // Add smooth scrolling to internal links
  enhanced = enhanced.replace(
    /<a\s+href="#([^"]+)"([^>]*)>/gi,
    '<a href="#$1" class="scroll-to-heading"$2>'
  );

  // Add responsive image classes
  enhanced = enhanced.replace(
    /<img([^>]*)>/gi,
    '<img$1 class="max-w-full h-auto rounded-lg shadow-md">'
  );

  // Add table styling
  enhanced = enhanced.replace(
    /<table([^>]*)>/gi,
    '<table$1 class="w-full border-collapse border border-gray-300 my-4">'
  );
  enhanced = enhanced.replace(
    /<th([^>]*)>/gi,
    '<th$1 class="border border-gray-300 px-4 py-2 bg-gray-100 font-semibold text-left">'
  );
  enhanced = enhanced.replace(
    /<td([^>]*)>/gi,
    '<td$1 class="border border-gray-300 px-4 py-2">'
  );

  // Add blockquote styling
  enhanced = enhanced.replace(
    /<blockquote([^>]*)>/gi,
    '<blockquote$1 class="border-l-4 border-amber-500 pl-4 italic bg-amber-50 py-2 my-4">'
  );

  // Add code block styling
  enhanced = enhanced.replace(
    /<pre([^>]*)>/gi,
    '<pre$1 class="bg-gray-100 p-4 rounded-lg overflow-x-auto my-4">'
  );

  // Add inline code styling
  enhanced = enhanced.replace(
    /<code([^>]*)>/gi,
    '<code$1 class="bg-gray-100 px-2 py-1 rounded text-sm font-mono">'
  );

  // Add list styling
  enhanced = enhanced.replace(
    /<ul([^>]*)>/gi,
    '<ul$1 class="list-disc list-inside space-y-1 my-2">'
  );
  enhanced = enhanced.replace(
    /<ol([^>]*)>/gi,
    '<ol$1 class="list-decimal list-inside space-y-1 my-2">'
  );

  // Add heading styling with scroll-margin for proper scrolling
  enhanced = enhanced.replace(
    /<h1([^>]*)>/gi,
    '<h1$1 class="text-3xl font-bold text-amber-800 mt-8 mb-4 scroll-mt-24">'
  );
  enhanced = enhanced.replace(
    /<h2([^>]*)>/gi,
    '<h2$1 class="text-2xl font-bold text-amber-800 mt-6 mb-3 scroll-mt-24">'
  );
  enhanced = enhanced.replace(
    /<h3([^>]*)>/gi,
    '<h3$1 class="text-xl font-bold text-amber-800 mt-4 mb-2 scroll-mt-24">'
  );
  enhanced = enhanced.replace(
    /<h4([^>]*)>/gi,
    '<h4$1 class="text-lg font-bold text-amber-800 mt-3 mb-2 scroll-mt-24">'
  );

  // Add paragraph styling
  enhanced = enhanced.replace(
    /<p([^>]*)>/gi,
    '<p$1 class="mb-4 leading-relaxed">'
  );

  // Add a marker to the last paragraph for lead popup detection
  const paragraphs = enhanced.match(/<p[^>]*>.*?<\/p>/gs);
  if (paragraphs && paragraphs.length > 0) {
    const lastParagraph = paragraphs[paragraphs.length - 1];
    const markedLastParagraph = lastParagraph.replace(
      /<p([^>]*)>/,
      '<p$1 data-last-paragraph="true">'
    );
    enhanced = enhanced.replace(lastParagraph, markedLastParagraph);
  }

  return enhanced;
};

/**
 * Extracts headings from HTML content for table of contents
 * @param html HTML content
 * @returns Array of heading objects with id, text, and level
 */
export const extractHeadings = (html: string): { id: string; text: string; level: number }[] => {
  const headings: { id: string; text: string; level: number }[] = [];
  
  try {
    // Check if DOMParser is available
    if (typeof DOMParser === 'undefined') {
      console.warn('DOMParser not available, using regex fallback');
      return extractHeadingsWithRegex(html);
    }
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Select all h2, h3, and h4 elements
    const headingElements = doc.querySelectorAll('h2, h3, h4');
    
    headingElements.forEach((heading) => {
      const text = heading.textContent?.trim() || '';
      if (!text) return;
      
      // Generate an ID from the heading text
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      // Determine heading level
      const level = parseInt(heading.tagName.substring(1));
      
      headings.push({ id, text, level });
    });
  } catch (error) {
    console.warn('Error parsing HTML with DOMParser, using regex fallback:', error);
    return extractHeadingsWithRegex(html);
  }
  
  return headings;
};

/**
 * Fallback function to extract headings using regex
 * @param html HTML content
 * @returns Array of heading objects with id, text, and level
 */
const extractHeadingsWithRegex = (html: string): { id: string; text: string; level: number }[] => {
  const headings: { id: string; text: string; level: number }[] = [];
  
  // Regex patterns for h2, h3, h4 tags
  const headingPatterns = [
    { level: 2, regex: /<h2[^>]*>(.*?)<\/h2>/gi },
    { level: 3, regex: /<h3[^>]*>(.*?)<\/h3>/gi },
    { level: 4, regex: /<h4[^>]*>(.*?)<\/h4>/gi }
  ];
  
  headingPatterns.forEach(({ level, regex }) => {
    let match;
    while ((match = regex.exec(html)) !== null) {
      const text = match[1].replace(/<[^>]*>/g, '').trim(); // Remove any nested HTML tags
      if (text) {
        // Generate an ID from the heading text
        const id = text
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim();
        
        headings.push({ id, text, level });
      }
    }
  });
  
  return headings;
};

/**
 * Handles URL hash changes and scrolls to the corresponding section
 * @param hash The URL hash (e.g., "#section-id")
 */
export const handleHashChange = (hash: string) => {
  if (hash) {
    const id = hash.substring(1); // Remove the # symbol
    const element = document.getElementById(id);
    if (element) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        const headerHeight = 120;
        const elementPosition = element.offsetTop - headerHeight;
        
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }, 100);
    }
  }
};

/**
 * Generates a table of contents component from headings with smooth scrolling
 * @param headings Array of heading objects
 * @returns JSX for table of contents
 */
export const TableOfContents = ({ headings }: { headings: { id: string; text: string; level: number }[] }) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Create intersection observer to track active sections
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-20% 0px -35% 0px', // Adjust for header height and better detection
        threshold: 0
      }
    );

    // Observe all heading elements
    headings.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) {
        observerRef.current?.observe(element);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [headings]);

  const handleTocClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    
    const element = document.getElementById(id);
    if (element) {
      // Calculate offset for fixed header (adjust as needed)
      const headerHeight = 120; // Approximate header height
      const elementPosition = element.offsetTop - headerHeight;
      
      // Smooth scroll to the element
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      
      // Update URL hash without triggering scroll
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  if (headings.length === 0) {
    return null;
  }

  return (
    <div className="bg-amber-50 rounded-lg p-6 sticky top-24 mb-8">
      <h3 className="text-lg font-bold text-amber-800 mb-4">Table of Contents</h3>
      <nav>
        <ul className="space-y-2">
          {headings.map((heading, index) => (
            <li 
              key={index} 
              className={`${
                heading.level === 2 ? 'ml-0' : 
                heading.level === 3 ? 'ml-4' : 
                'ml-8'
              }`}
            >
              <a 
                href={`#${heading.id}`}
                onClick={(e) => handleTocClick(e, heading.id)}
                className={`block py-1 px-2 rounded transition-all duration-200 ${
                  activeSection === heading.id
                    ? 'text-amber-900 bg-amber-200 font-medium'
                    : 'text-amber-700 hover:text-amber-900 hover:bg-amber-100'
                }`}
              >
                {heading.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}; 